import {
  computed,
  nextTick,
  ref,
  watch,
  type ComputedRef,
  type Ref,
  type WritableComputedRef,
} from 'vue'
import type { QInputNativeElement } from 'quasar'
import type { PValidationRule } from '../../../types/types'

export type PDecimalSeparator = ',' | '.'

type UseNumberInputOptions = {
  model: Ref<string | number | null>
  isDecimal: Ref<boolean>
  decimals: Ref<null | number>
  rules: Ref<PValidationRule[]>
  locale: Ref<string>
  nativeElement: () => QInputNativeElement | undefined
}

export type UseNumberInputReturn = {
  decimalText: Ref<string>
  inputValue: WritableComputedRef<string | number | null | undefined>
  inputRules: ComputedRef<PValidationRule[]>
  onBeforeInput: (event: Event) => void
  formatOnBlur: () => void
}

const isDigit = (char: string): boolean => char >= '0' && char <= '9'

const isSeparator = (char: string): boolean => char === ',' || char === '.'

export const decimalSeparatorForLocale = (locale: string): PDecimalSeparator => {
  try {
    const part = new Intl.NumberFormat(locale)
      .formatToParts(1.1)
      .find((candidate) => candidate.type === 'decimal')

    return part?.value === ',' ? ',' : '.'
  } catch {
    return '.'
  }
}

const isGroupedNumber = (groups: string[]): boolean => {
  return (
    groups.length > 2 &&
    groups[0].length > 0 &&
    groups[0].length <= 3 &&
    groups.slice(1, -1).every((group) => group.length === 3)
  )
}

const collapseGroupedNumber = (body: string, groups: string[]): string => {
  const separators = body.split('').filter((char) => isSeparator(char))
  const decimal = separators[separators.length - 1]
  const lastGroup = groups[groups.length - 1]
  const endsWithDecimals = lastGroup.length !== 3 || separators.some((char) => char !== decimal)

  return endsWithDecimals ? groups.slice(0, -1).join('') + decimal + lastGroup : groups.join('')
}

const keepSingleSeparator = (body: string): string => {
  const afterDigit = body.search(/\d[.,]/)
  const decimalIndex = afterDigit === -1 ? body.search(/[.,]/) : afterDigit + 1

  return body
    .split('')
    .filter((char, index) => !isSeparator(char) || index === decimalIndex)
    .join('')
}

const capFractionDigits = (text: string, decimals: null | number): string => {
  const separatorIndex = text.search(/[.,]/)

  if (decimals === null || separatorIndex === -1) {
    return text
  }

  if (decimals === 0) {
    return text.slice(0, separatorIndex)
  }

  return text.slice(0, separatorIndex + 1 + decimals)
}

export const sanitizeNumericInput = (raw: string, decimals: null | number = null): string => {
  let filtered = ''

  for (const char of raw) {
    if (isDigit(char) || isSeparator(char) || (char === '-' && filtered.length === 0)) {
      filtered += char
    }
  }

  const sign = filtered.startsWith('-') ? '-' : ''
  const body = sign === '' ? filtered : filtered.slice(1)
  const groups = body.split(/[.,]/)

  if (groups.length === 1) {
    return capFractionDigits(filtered, decimals)
  }

  return capFractionDigits(
    sign +
      (isGroupedNumber(groups) ? collapseGroupedNumber(body, groups) : keepSingleSeparator(body)),
    decimals,
  )
}

export const parseNumericInput = (text: string): number | null => {
  const normalized = text.replace(',', '.')
  const value = Number(normalized)

  return normalized === '' || Number.isNaN(value) ? null : value
}

export const toNumericModelValue = (value: string | number | null | undefined): number | null => {
  if (typeof value === 'string') {
    return parseNumericInput(sanitizeNumericInput(value))
  }

  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

export const formatNumericModelValue = (
  value: string | number | null | undefined,
  decimalSeparator: PDecimalSeparator,
  decimals: null | number = null,
): string => {
  const numeric = toNumericModelValue(value)

  if (numeric === null) {
    return ''
  }

  const text = decimals === null ? String(numeric) : numeric.toFixed(decimals)

  return text.replace('.', decimalSeparator)
}

export const useNumberInput = ({
  model,
  isDecimal,
  decimals,
  rules,
  locale,
  nativeElement,
}: UseNumberInputOptions): UseNumberInputReturn => {
  const decimalSeparator = computed<PDecimalSeparator>(() => {
    return decimalSeparatorForLocale(locale.value)
  })

  const format = (value: string | number | null | undefined): string => {
    return formatNumericModelValue(value, decimalSeparator.value, decimals.value)
  }

  const decimalText = ref<string>(isDecimal.value ? format(model.value) : '')

  // the field keeps the sanitized text, the native input still holds what was typed
  const syncNativeValue = (): void => {
    const element = nativeElement()

    if (element && element.value !== decimalText.value) {
      element.value = decimalText.value
    }
  }

  const inputValue = computed<string | number | null | undefined>({
    get: () => {
      return isDecimal.value ? decimalText.value : model.value
    },
    set: (value) => {
      if (!isDecimal.value) {
        model.value = value ?? null

        return
      }

      const typed = value === null || value === undefined ? '' : String(value)
      const text = sanitizeNumericInput(typed, decimals.value)
      const nextModel = parseNumericInput(text)

      decimalText.value = text

      if (nextModel !== model.value) {
        model.value = nextModel
      }

      if (text !== typed) {
        nextTick(syncNativeValue)
      }
    },
  })

  const inputRules = computed<PValidationRule[]>(() => {
    if (!isDecimal.value) {
      return rules.value
    }

    return rules.value.map((rule) => {
      return () => rule(parseNumericInput(decimalText.value))
    })
  })

  const onBeforeInput = (event: Event): void => {
    const inputEvent = event as InputEvent

    if (
      !isDecimal.value ||
      inputEvent.inputType !== 'insertText' ||
      inputEvent.data?.length !== 1
    ) {
      return
    }

    const element = inputEvent.target as HTMLInputElement
    const start = element.selectionStart ?? element.value.length
    const end = element.selectionEnd ?? start
    const candidate = element.value.slice(0, start) + inputEvent.data + element.value.slice(end)

    if (sanitizeNumericInput(candidate, decimals.value) !== candidate) {
      event.preventDefault()
    }
  }

  const formatOnBlur = (): void => {
    if (!isDecimal.value) {
      return
    }

    decimalText.value = format(parseNumericInput(decimalText.value))
  }

  watch(model, (value) => {
    if (!isDecimal.value) {
      return
    }

    if (toNumericModelValue(value) !== parseNumericInput(decimalText.value)) {
      decimalText.value = format(value)
    }
  })

  return {
    decimalText,
    inputValue,
    inputRules,
    onBeforeInput,
    formatOnBlur,
  }
}
